import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/images/pulso-logo.png';
import '../styles/pulso.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const token = searchParams.get('token');

  const [invite, setInvite] = useState(null);
  const [validating, setValidating] = useState(true);
  const [validationError, setValidationError] = useState('');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const submittedRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setValidationError('Missing invitation token. Please use the link from your email.');
      setValidating(false);
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/team/invite/${token}`);
        const data = await res.json();
        if (!res.ok || !data.success) {
          setValidationError(data.error || 'Invitation is invalid or has expired.');
        } else {
          setInvite(data.invite);
        }
      } catch (err) {
        setValidationError('Could not validate invitation. Please try again.');
      }
      setValidating(false);
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    console.log('[AcceptInvite] handleSubmit called');

    if (submittedRef.current) {
      console.log('[AcceptInvite] BLOCKED - already submitted');
      return;
    }
    submittedRef.current = true;

    setError('');

    if (password !== confirm) {
      setError('Passwords do not match');
      submittedRef.current = false;
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      submittedRef.current = false;
      return;
    }

    setIsLoading(true);

    let res;
    let data;

    try {
      console.log('[AcceptInvite] Sending request...');
      res = await fetch(`${API_URL}/api/admin/team/accept-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });

      console.log('[AcceptInvite] Response status:', res.status);
      data = await res.json();
      console.log('[AcceptInvite] Response data:', data);
    } catch (err) {
      console.error('[AcceptInvite] FETCH FAILED:', err);
      setError('Network error. Please check your connection.');
      setIsLoading(false);
      submittedRef.current = false;
      return;
    }

    if (!res.ok || !data.success) {
      console.log('[AcceptInvite] Server returned error:', data.error);
      setError(data.error || 'Something went wrong. Please try again.');
      setIsLoading(false);
      submittedRef.current = false;
      return;
    }

    // Success - log them in and redirect
    console.log('[AcceptInvite] SUCCESS - logging in and redirecting');
    try {
      loginWithToken(data.token, data.user);
      navigate('/messages', { replace: true });
    } catch (err) {
      console.error('[AcceptInvite] Error during login/navigate:', err);
      setError('Account created but login failed. Please log in manually.');
      setIsLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: '#395b81' }} />
      </div>
    );
  }

  if (validationError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-md animate-fade-in">
          <div className="mb-8 flex flex-col items-center text-center">
            <div className="mb-4">
              <img src={logo} alt="Pulso" style={{ width: 56, height: 'auto' }} />
            </div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#38394e' }}>PULSO</h1>
            <p className="mt-1 text-muted-foreground">by Imaginarium</p>
          </div>

          <div className="rounded-lg border border-border bg-card text-card-foreground shadow-sm">
            <div className="p-6">
              <div className="rounded-lg bg-red-50 border border-red-200 p-4 flex items-start gap-3 mb-4">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-800">
                  <p className="font-medium mb-1">Invitation problem</p>
                  <p>{validationError}</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground" style={{ marginBottom: 16 }}>
                Ask the person who invited you to send a new invitation.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors h-11 w-full"
                style={{ background: '#f0856d', color: 'white', border: 'none' }}
              >
                Go to login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md animate-fade-in">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-4">
            <img src={logo} alt="Pulso" style={{ width: 56, height: 'auto' }} />
          </div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#38394e' }}>PULSO</h1>
          <p className="mt-1 text-muted-foreground">by Imaginarium</p>
        </div>

        <div className="rounded-lg border border-border bg-card text-card-foreground shadow-sm">
          <div className="flex flex-col space-y-1.5 p-6">
            <h3 className="text-xl font-semibold leading-none tracking-tight">
              Welcome, {invite.name.split(' ')[0]}
            </h3>
            <p className="text-sm text-muted-foreground">
              Set a password to activate your {invite.role === 'superadmin' ? 'superadmin' : 'admin'} account ({invite.email})
            </p>
          </div>

          <div className="p-6 pt-0">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium leading-none">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    disabled={isLoading}
                    autoFocus
                    className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 pr-10 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="confirm" className="text-sm font-medium leading-none">
                  Confirm password
                </label>
                <input
                  id="confirm"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Re-enter your password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={8}
                  disabled={isLoading}
                  className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-11 w-full"
                style={{ background: '#f0856d', color: 'white', border: 'none' }}
                onMouseEnter={e => { if (!isLoading) e.currentTarget.style.opacity = '0.88'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Activating...
                  </>
                ) : (
                  'Activate account & sign in'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AcceptInvite;